import React from 'react';
import { Link } from 'react-router-dom';
import { Truck, Clock, PackageCheck, MapPin, Euro, Search, ShieldCheck, ArrowRight } from 'lucide-react';
import PageShell from '../components/PageShell';
import { useShippingSummary, fmtEuro } from '../services/shippingInfo';
import { useT } from '../services/i18n';

const CARRIERS = [
  { name: 'Colissimo', delay: 'ship.c1Delay', note: 'ship.c1Note', color: 'bg-yellow-400 text-gray-900' },
  { name: 'Chronopost', delay: 'ship.c2Delay', note: 'ship.c2Note', color: 'bg-blue-600 text-white' },
  { name: 'DPD', delay: 'ship.c3Delay', note: 'ship.c3Note', color: 'bg-red-600 text-white' },
  { name: 'Mondial Relay', delay: 'ship.c4Delay', note: 'ship.c4Note', color: 'bg-[#1e3a5f] text-white' },
];

const STEPS = [
  { icon: PackageCheck, title: 'ship.s1', text: 'ship.s1Text' },
  { icon: Clock, title: 'ship.s2', text: 'ship.s2Text' },
  { icon: Truck, title: 'ship.s3', text: 'ship.s3Text' },
  { icon: MapPin, title: 'ship.s4', text: 'ship.s4Text' },
];

const ShippingPage = () => {
  const { t } = useT();
  const ship = useShippingSummary();
  const free = ship.freeFrom != null ? fmtEuro(ship.freeFrom) : null;
  const fee = ship.price != null ? fmtEuro(ship.price) : '—';
  return (
  <PageShell title={t('ship.title')} description={`${t('ship.metaDesc')}${free ? ` ${t('ship.metaFree', { free })}` : ''}`} path="/livraison" eyebrow={t('ship.eyebrow')} heading={free ? t('ship.headingFree', { free }) : t('ship.heading')} intro={t('ship.intro')} testId="shipping-page" wide>
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 -mt-16 sm:-mt-20 relative mb-12">
      {[[Euro, t('ship.fees'), free ? t('ship.feesFree', { fee, free }) : fee], [Clock, t('ship.avgDelay'), t('ship.avgDelayValue')], [ShieldCheck, t('ship.insured'), t('ship.insuredValue')]].map(([I, label, v]) => (
        <div key={label} className="bg-white rounded-2xl shadow-lg p-5 flex items-center gap-4"><span className="w-12 h-12 rounded-xl bg-orange-50 text-orange-500 flex items-center justify-center flex-shrink-0"><I size={22} /></span><div><p className="text-xs uppercase tracking-wide text-gray-500">{label}</p><p className="font-bold text-gray-900">{v}</p></div></div>
      ))}
    </div>

    <h2 className="text-base md:text-lg font-bold text-gray-900 mb-4">{t('ship.how')}</h2>
    <ol className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-12" data-testid="shipping-steps">
      {STEPS.map((s, i) => (
        <li key={s.title} className="bg-white rounded-xl p-5 shadow-sm relative">
          <span className="absolute -top-3 left-5 bg-orange-500 text-white text-xs font-bold w-7 h-7 rounded-full flex items-center justify-center">{i + 1}</span>
          <s.icon size={26} className="text-[#1e3a5f] mb-3 mt-1" />
          <p className="font-semibold text-gray-900">{t(s.title)}</p><p className="text-sm text-gray-600 mt-1">{t(s.text)}</p>
        </li>
      ))}
    </ol>

    <h2 className="text-base md:text-lg font-bold text-gray-900 mb-4">{t('ship.carriers')}</h2>
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-12" data-testid="shipping-carriers">
      {CARRIERS.map(c => (
        <div key={c.name} className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className={`${c.color} px-5 py-3 font-bold tracking-wide`}>{c.name}</div>
          <div className="p-5"><p className="text-2xl font-bold text-gray-900">{t(c.delay)}</p><p className="text-sm text-gray-600 mt-1">{t(c.note)}</p></div>
        </div>
      ))}
    </div>

    <div className="bg-[#0e0e10] text-white rounded-2xl p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
      <div><p className="text-xs font-semibold uppercase tracking-[0.2em] text-orange-400 mb-1">{t('ship.trackingKicker')}</p><h3 className="text-lg md:text-xl font-bold">{t('ship.trackingTitle')}</h3><p className="text-gray-300 text-sm mt-1">{t('ship.trackingText')}</p></div>
      <Link to="/mes-commandes" data-testid="shipping-track-link" className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-semibold px-5 py-3 rounded-full whitespace-nowrap"><Search size={16} /> {t('ship.trackCta')} <ArrowRight size={16} /></Link>
    </div>
  </PageShell>
  );
};

export default ShippingPage;
